"use client"

import { useCallback } from "react"

import { useTracking } from "@/app/contexts/tracking-context"
import {
    createEventId,
    facebookConversionTrack,
    tiktokConversionTrack,
    tiktokClickIdentity,
} from "@/app/utils/health-tracking"

export interface LeadConversionParams {
    // Event dataLayer écouté par les tags Google Ads/GA. Varie selon le point d'entrée
    // (ne pas uniformiser : les triggers GTM en dépendent). Ex: 'gtm_loan.conversion' ou 'conversion'.
    googleEvent: string
    productType: string
    // Nom de l'event Meta Conversions API (ex: 'Identity sent (API)').
    fbEventName: string
    // Event standard TikTok Events API (défaut: 'SubmitForm').
    tiktokEventName?: string
    email?: string | null
    phone?: string | null
    token?: string | null
}

/**
 * Déclenche une conversion "lead" sur les 3 canaux à la fois — Google Ads (dataLayer),
 * Meta Conversions API et TikTok Events API — avec un `event_id` partagé entre le pixel
 * client (GTM) et l'Events API serveur TikTok pour la déduplication.
 *
 * Point d'entrée unique appelé à chaque génération de lead (fin de parcours AE + formulaires
 * de rappel), pour garantir une couverture cohérente entre canaux.
 */
export const useLeadConversionTracking = () => {
    const { trackWithEmailHash } = useTracking()

    return useCallback(
        ({
            googleEvent,
            productType,
            fbEventName,
            tiktokEventName = "SubmitForm",
            email,
            phone,
            token,
        }: LeadConversionParams) => {
            const eventId = createEventId()
            const userData = { email: email ?? "", phone: phone ?? "" }
            const customData = { token: token ?? "" }
            const tiktokUserData = { ...userData, ...tiktokClickIdentity() }

            // Google Ads / GA — le `tiktok_event_id` permet au tag pixel TikTok (GTM) de dédupliquer.
            void trackWithEmailHash(googleEvent, email, { productType, tiktok_event_id: eventId })

            // Meta Conversions API (serveur).
            facebookConversionTrack({ event_name: fbEventName, user_data: userData, custom_data: customData })

            // TikTok Events API (serveur).
            tiktokConversionTrack({
                event_name: tiktokEventName,
                user_data: tiktokUserData,
                custom_data: customData,
                event_id: eventId,
            })
        },
        [trackWithEmailHash],
    )
}
